import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CaseManagementService } from '../case-management/case-management.service';
import { AuthenticationService } from '../authentication.service';
import { IoConfigurationFeatureCodes, FeatureActionsMap } from '../../classes/authentication/user.class';
import { DeviceService } from '../device/device.service';
import {
  SampleDeliveryRequest,
  SdrAllocationDisplay,
  SdrCustomerAllocation,
  SdrMeetingWindow,
  SdrIoConfigLimit,
  SdrPickerSku,
  SdrProductLine,
  SdrStatus,
  isValidEmail,
} from '../../classes/sample/sample-delivery-request.class';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import {
  SampleDeliveryRequestDataService,
  SdrCreateResult,
  StagedSdrNote,
} from '../../data-services/sample/sample-delivery-request.data.service';
import { IONote } from '../../classes/io/io-note.class';
import { NotesAttachmentsConfig, parseNotesAttachmentsConfig } from '@omni/utility/notes-attachments-config.util';

export interface SdrFormState {
  contactId: string;
  contactName: string;
  deliveryAddressId: string;
  deliveryAddressLabel: string;
  emailId: string;
  emailLabel: string;
  products: SdrProductLine[];
  /** True when the rep typed a free-text delivery address instead of picking a saved one. */
  useAlternateAddress: boolean;
  /** Free-text delivery address (persisted to indskr_alternativeaddress). */
  alternateDeliveryAddress: string;
  /** True when the rep typed a free-text email instead of picking a saved one. */
  useAlternateEmail: boolean;
  /** Free-text email (persisted to indskr_alternativeemailaddress). */
  alternateEmail: string;
}

/**
 * Resolved view of the BU_ACTIVITY_GA_NOTES_ATTACHMENTS iO config — drives which
 * inputs render in the Notes & Attachments section and whether content is
 * required to submit.
 */
export type SdrNotesConfig = NotesAttachmentsConfig;

/** Submit-time notes/attachments requirement, supplied by the create form. */
export interface SdrNotesGate {
  notesMandatory: boolean;
  notesSatisfied: boolean;
  attachmentsMandatory: boolean;
  attachmentsSatisfied: boolean;
}

/**
 * Thrown by submit()/submitOffline()/InlineSampleDeliveryRequestComponent's
 * own finalize path when revalidateAllocationsBeforeSubmit's fresh,
 * immediately-pre-submit re-check finds a line whose quantity now exceeds
 * what's actually available — closing the race where two drafts for the
 * same contact+SKU were both created against the same stale "remaining"
 * snapshot. Carries the specific rejected lines so the caller can name them
 * in a toast, matching notifySubmitOutcome's existing convention.
 */
export class SdrAllocationExceededError extends Error {
  constructor(public readonly blockedProducts: SdrProductLine[]) {
    super('SDR_ALLOCATION_EXCEEDED');
  }
}

@Injectable({ providedIn: 'root' })
export class SampleDeliveryRequestService {
  submittedSdr: SampleDeliveryRequest | null = null;

  /**
   * In-flight and unconfirmed-failed submit attempts, keyed by the caller-supplied
   * client SDR id (the standalone form and the inline meeting form each mint their
   * own id per draft and pass it into submit()/submitOffline()/reconcileSubmit()).
   * Scoped per id — NOT a single shared flag — so a submit in one meeting/form can
   * never block or clobber a different meeting/form's own attempt. A single shared
   * `isSubmitting`/`pendingReconcileId` previously meant opening a fresh draft
   * anywhere in the app (which unconditionally called clearPendingReconcile()) could
   * wipe out a DIFFERENT, still-unresolved draft's reconcile id — risking a
   * duplicate re-post of that other draft once its rep retried it.
   */
  private submittingIds = new Set<string>();

  /**
   * Client ids of submits that did NOT confirm success (they threw). Added before
   * the create batch, removed the instant it resolves; left set on failure so the
   * owning form can reconcile against the server before a blind retry (a network
   * drop on the response leg can commit the record while the client sees failure —
   * re-posting would duplicate it). In-memory only, so an app reload clears it for
   * free. See `reconcileSubmit` / `clearPendingReconcile`.
   */
  private pendingReconcileIds = new Set<string>();

  /** Whether the given client SDR id currently has a submit in flight. */
  isSubmittingId(id: string): boolean {
    return this.submittingIds.has(id);
  }

  /** Whether the given client SDR id has an unconfirmed-failed submit awaiting reconcile. */
  hasPendingReconcile(id: string): boolean {
    return this.pendingReconcileIds.has(id);
  }

  constructor(
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly caseManagementService: CaseManagementService,
    private readonly authService: AuthenticationService,
    private readonly dataService: SampleDeliveryRequestDataService,
    private readonly device: DeviceService,
  ) {
    // uploadPendingSdrs (data service, background offline sync) already wrote
    // the server-hydrated record to disk — this just mirrors it into the
    // in-memory cache fetchSdrsForMeeting's offline branch actually reads
    // from, so a meeting reopened offline (without ever going through an
    // online meeting-open or the separate syncRepSdrs pipeline task) shows the
    // real name/id instead of the pre-sync placeholder. Pure in-memory
    // upsert, no disk write (already done) and no network call — offline-safe
    // by construction. Root-provided singleton, lives for the app's lifetime —
    // no explicit teardown needed.
    this.dataService.sdrSynced$.subscribe((sdr) => {
      if (!sdr?.id) return;
      const idx = this.allSdrs.findIndex((s) => s.id === sdr.id);
      if (idx >= 0) this.allSdrs[idx] = sdr;
      else this.allSdrs = [sdr, ...this.allSdrs];
      this.allSdrs$.next(this.allSdrs);
      // A replayed offline SDR that reached Submitted has just had its
      // quantities decremented by Dynamics in that same transaction. Drop the
      // cached snapshot so the next read re-fetches the decremented figure
      // instead of serving the pre-upload one until the TTL lapses — this is
      // what makes "deduct on submission" hold for the offline→online path,
      // where no interactive submit ran on this device to clear it.
      if (sdr.statusCode === SdrStatus.Submitted) {
        this.clearManualAllocationsForContact(sdr.contactId);
      }
    });
  }

  /** Device-wide cache of the rep's own SDRs (analog of activityService.activities). */
  allSdrs: SampleDeliveryRequest[] = [];

  /**
   * Set once allSdrs has been populated from local disk at least once this
   * session — either by the routine syncRepSdrs() sync-pipeline task, or by
   * fetchSdrsForMeeting's own fallback hydration below. Sample Drop's
   * equivalent (activityService.activities) never needs this: it's populated
   * by a load step that BLOCKS the rest of the app's navigation, so it's
   * always already loaded by the time any meeting can be opened. allSdrs has
   * no such guarantee — syncRepSdrs is just one of ~30 tasks in master.data.
   * service's bounded-concurrency sync pool, with no ordering relationship to
   * when the rep can open a meeting. Without this fallback, opening a
   * meeting offline shortly after an app relaunch (before that pooled task
   * gets its turn) finds allSdrs still empty — and offline has no live
   * network call to fall back on, so the Request tab shows nothing until the
   * pooled task eventually runs (OMNI-61497 follow-up).
   */
  private _allSdrsHydrated = false;
  private _allSdrsHydrationPromise: Promise<void> | null = null;

  /** Local-disk-only hydration, run at most once per session (memoized), so
   *  any caller — the routine sync task or a meeting opening cold — can
   *  guarantee allSdrs reflects local disk without redundant re-reads once
   *  either path has already done so. */
  private async _ensureAllSdrsHydrated(): Promise<void> {
    if (this._allSdrsHydrated) return;
    if (!this._allSdrsHydrationPromise) {
      this._allSdrsHydrationPromise = (async () => {
        this.allSdrs = await this.dataService.loadAllSdrRecordsFromDb();
        this.allSdrs$.next(this.allSdrs);
        this._allSdrsHydrated = true;
      })();
    }
    await this._allSdrsHydrationPromise;
  }

  /**
   * Emits the rep SDR cache whenever it is (re)loaded — drives the
   * status-change notification generator (analog of quotesService.quotesList$).
   */
  allSdrs$ = new BehaviorSubject<SampleDeliveryRequest[]>([]);

  /**
   * Refresh the device-wide rep SDR cache. Hydrates from db first (so offline
   * app start has data), then — if online — fetches fresh, windows by the
   * offline-data age limit, and overwrites db + memory. Fetch-and-overwrite; no
   * delta. FA-gated.
   */
  async syncRepSdrs(loadFromDbOnly = false): Promise<void> {
    if (!this.authService.hasFeatureAction(FeatureActionsMap.SAMPLE_DELIVERY_REQUESTS)) {
      this.allSdrs = [];
      this.allSdrs$.next(this.allSdrs);
      return;
    }
    this.allSdrs = await this.dataService.loadAllSdrRecordsFromDb();
    this.allSdrs$.next(this.allSdrs);
    // This IS the local-disk hydration _ensureAllSdrsHydrated falls back to —
    // mark it done so a meeting opened after this already-completed sync
    // doesn't redundantly re-read disk.
    this._allSdrsHydrated = true;
    if (this.device.isOffline || loadFromDbOnly) return;
    try {
      const fetched = await this.dataService.fetchRepSdrs();
      const limitMs = this.authService.getPastOfflineDataLimitDateInUTCMillisecond();
      const fresh = fetched.filter(
        (s) => !!s.createdOn && s.createdOn.getTime() >= limitMs && s.statusCode !== SdrStatus.Inactive,
      );
      // Records still awaiting upload (createSdrOffline/uploadPendingSdrs) never
      // appear in `fetched` — the server has never seen them. They must NOT be
      // treated as "server dropped this" and pruned: sdr_record_<id> is now the
      // ONLY doc for a pending SDR (no separate sdr_pending_ store), so deleting
      // it here would destroy the record before uploadPendingSdrs ever replays
      // it (OMNI-59706 — offline SDRs silently never reaching Dynamics).
      const pendingIds = new Set(this.allSdrs.filter((s) => s.pendingSync).map((s) => s.id));
      const oldIds = new Set(this.allSdrs.map((s) => s.id));
      const newIds = new Set(fresh.map((s) => s.id));
      const dropped = [...oldIds].filter((id) => !newIds.has(id) && !pendingIds.has(id));
      console.log(`[SDR-SYNC] syncRepSdrs: fetched=${fetched.length} fresh=${fresh.length} dropping=${dropped.length} pendingProtected=${pendingIds.size}`);
      await Promise.all(dropped.map((id) => this.dataService.deleteSdrRecordFromDb(id)));
      await Promise.all(fresh.map((s) => this.dataService.saveSdrRecordToDb(s)));
      const pendingStillLocal = this.allSdrs.filter((s) => pendingIds.has(s.id));
      this.allSdrs = [...pendingStillLocal, ...fresh];
      this.allSdrs$.next(this.allSdrs);
    } catch (err) {
      console.error('[SDR-SYNC] syncRepSdrs: failed; keeping db-loaded SDRs', err);
    }
  }

  /** The rep's own SDRs for a contact, from the device-wide cache (offline read). */
  getRepSdrsForContact(contactId: string): SampleDeliveryRequest[] {
    return this.allSdrs.filter((s) => s.contactId === contactId);
  }

  /**
   * Whether a meeting has any SUBMITTED SDR (offline-pending finalize or
   * synced) raised against it — gates the meeting-scrap HARD block
   * (OMNI-61272/ActivityScrapStatus.HASSUBMITTEDSDR). Explicitly excludes a
   * Draft (statusCode Active, from createSdrDraft) — a still-being-edited
   * Draft only gets the soft warning, see hasOpenSdrForMeeting.
   */
  hasSdrForMeeting(appointmentId: string): boolean {
    return !!appointmentId
      && (this.allSdrs || []).some((s) => s.appointmentId === appointmentId && s.statusCode !== SdrStatus.Active);
  }

  /**
   * Whether a meeting has an OPEN (Draft, statusCode Active, not yet
   * finalized) SDR raised against it — gates the meeting-scrap SOFT warning
   * (OMNI-61316 AC1), mirroring Sample Drop's HASOPENINMEETINGALLOCATIONS
   * treatment (a warning appended to the scrap popup, not a hard block).
   * Called directly by the meeting-scrap soft-warning check (OMNI-61316 AC1,
   * go-app-activities-page.component.ts) — no ActivityScrapStatus value
   * needed; unlike HASOPENINMEETINGALLOCATIONS/HASSUBMITTEDSDR, this doesn't
   * gate through the enum.
   */
  hasOpenSdrForMeeting(appointmentId: string): boolean {
    return !!appointmentId
      && (this.allSdrs || []).some((s) => s.appointmentId === appointmentId && s.statusCode === SdrStatus.Active);
  }

  /**
   * Scrap (cancel) an in-progress Draft SDR — mirrors Sample Drop's per-
   * allocation scrap. The Dynamics record itself is kept (dataService.scrapSdr
   * flips statuscode to Inactive, a real audit trail), but it's dropped from
   * this cache/observable immediately so it stops appearing anywhere in the
   * UI (meeting tab bar, hasOpenSdrForMeeting's soft-warn gate, etc.)
   * without waiting for a server round-trip.
   */
  async scrapSdr(sdr: SampleDeliveryRequest): Promise<void> {
    await this.dataService.scrapSdr(sdr.id);
    this.allSdrs = (this.allSdrs || []).filter((s) => s.id !== sdr.id);
    this.allSdrs$.next(this.allSdrs);
    if (!this.device.isOffline) {
      // Online: Dynamics is already patched — nothing left pending for this
      // device. Drop the local cached copy so it can't reappear on the next
      // loadAllSdrRecordsFromDb(). Left in place when offline: dataService.
      // scrapSdr already stamped it (statuscode Inactive, pendingSync true)
      // for uploadPendingSdrScraps to reconcile on reconnect.
      try {
        await this.dataService.deleteSdrRecordFromDb(sdr.id);
      } catch (err) {
        console.error('scrapSdr: failed to remove local cached record', err);
      }
    }
  }

  /**
   * Per-contact cache of Manual-Distribution Customer Allocation records
   * (indskr_customersampleproduct), keyed by contactId then by lower-cased
   * skuId — the eligibility ledger behind the SDR limit-validation
   * client-side pre-check. Populated by [[syncManualAllocationsForContact]].
   *
   * Holds EVERY config row for a SKU, not just the one usable today. A
   * customer can have several non-overlapping config periods for the same
   * SKU, and which one applies depends on the date the request is for — the
   * meeting's own date for an in-meeting SDR (OMNI-61513). Collapsing here
   * baked today's answer into the cache and discarded the other rows, so a
   * meeting on any other date could never resolve the right one. The choice
   * now happens at read time, where that date is known.
   */
  private manualAllocationsByContact = new Map<string, Map<string, SdrCustomerAllocation[]>>();

  /** Wall-clock time (ms) each contact's cache was last successfully
   *  live-fetched — drives the TTL guard in syncManualAllocationsForContact. */
  private manualAllocationsSyncedAt = new Map<string, number>();

  /**
   * How long a contact's in-memory allocation cache is considered "fresh
   * enough" before syncManualAllocationsForContact will re-hit the network
   * for it. Short enough that data shown during one SDR-creation session
   * stays reasonably current; long enough to absorb a rep swapping between
   * contacts and back (e.g. A → B → A within a couple of minutes) without a
   * redundant re-fetch of A. This is purely a UX/network-cost optimization —
   * Dynamics remains authoritative at submit time regardless of how stale
   * this cache is.
   */
  private static readonly MANUAL_ALLOCATION_CACHE_TTL_MS = 3 * 60 * 1000;

  /**
   * Refresh the Manual-Distribution Customer Allocation cache for one
   * contact — called when the SDR create form opens/changes customer.
   * FA-gated (no-op if the rep doesn't have Sample Delivery Requests).
   *
   * Online: skips the network call entirely if this contact's cache is still
   * within MANUAL_ALLOCATION_CACHE_TTL_MS (e.g. a rapid contact swap back and
   * forth) — otherwise live-fetches, populates memory, and write-throughs to
   * disk (SDR_MANUAL_ALLOC) so a later offline session — or this same session
   * after a failed re-fetch — has a last-known snapshot instead of nothing.
   * Offline (or the live fetch itself fails): reads that same disk snapshot
   * instead of leaving the cache empty, so a contact synced moments before
   * losing connection doesn't silently lose pre-check coverage for the rest
   * of the offline session. Either way this is a best-effort optimistic
   * check, never the source of truth — Dynamics validates authoritatively at
   * submit time — so serving a stale disk snapshot (or a TTL-stale in-memory
   * one) is an acceptable degrade, not a correctness bug.
   *
   * Deliberately isolated from the Sample Drop feature's own Customer
   * Allocation sync (SampleDataService._fetchAndSaveCustomerSampleAllocations)
   * — see the FetchXML's own comment (dynamics-fetchQueries.ts) for why a
   * fresh, isolated, per-contact fetch (and its own disk snapshot) is used
   * instead of reusing that device-wide sync. See OMNI-59706 design discussion.
   */
  async syncManualAllocationsForContact(contactId: string, forceRefresh = false): Promise<void> {
    if (!contactId || !this.authService.hasFeatureAction(FeatureActionsMap.SAMPLE_DELIVERY_REQUESTS)) {
      return;
    }
    // OMNI-61507: nothing consumes the cache while validation is off (both
    // getAllocationForSku and isAllocationBlocking short-circuit), so skip the
    // fetch and the disk write entirely rather than warming a cache no one reads.
    if (!this.isAllocationValidationEnabled) {
      return;
    }
    if (this.device.isOffline) {
      await this._loadManualAllocationsFromDbIntoMemory(contactId);
      return;
    }
    if (!forceRefresh) {
      const lastSyncedAt = this.manualAllocationsSyncedAt.get(contactId);
      if (lastSyncedAt != null && Date.now() - lastSyncedAt < SampleDeliveryRequestService.MANUAL_ALLOCATION_CACHE_TTL_MS) {
        return;
      }
    }
    try {
      const skuIds = this.sdrSkus.map((s) => s.skuId).filter(Boolean);
      const allocations = await this.dataService.fetchManualAllocationsForContact(contactId, skuIds);
      this.manualAllocationsByContact.set(contactId, this._toSkuMap(allocations));
      this.manualAllocationsSyncedAt.set(contactId, Date.now());
      await this.dataService.saveManualAllocationsToDb(contactId, allocations);
    } catch (err) {
      console.error('syncManualAllocationsForContact live fetch failed; falling back to last disk snapshot', err);
      await this._loadManualAllocationsFromDbIntoMemory(contactId);
    }
  }

  /**
   * Bulk sibling of syncManualAllocationsForContact — refreshes the cache for
   * every contact mapped to the rep's own position(s) in one call (position
   * ids batched into a single FetchXML, mirroring
   * SampleDataService._fetchAndSaveCustomerSampleAllocations's own
   * mypositions/childPositions collection), instead of relying on the rep
   * having opened the SDR form for each contact individually. Meant to run
   * during the regular pull-sync pass — see master.data.service.ts. FA-gated
   * and a no-op offline (offline devices keep whatever per-contact disk
   * snapshots already exist; this method only refreshes from a live source).
   *
   * The underlying bulk FetchXML fans out two independent one-to-many joins
   * (consumption history, eligibility association) off the same config row —
   * FetchXML cross-joins those, so it returns up to (eligible contacts x
   * contacts-with-history) rows PER CONFIG (confirmed root cause of 35K+ rows
   * for a large territory, not 35K distinct facts — see
   * dataService.shouldRunFullPositionAllocationSync's own comment). That
   * multiplication survives a naive modifiedon filter, so the full bulk pull
   * only runs on the very first sync ever or when forceFullSync is true.
   *
   * Every other (delta) pass instead runs two independent-signal, but
   * cursor-sharing, checks:
   *  - Consumption: contacts whose OWN indskr_customersampleproduct record
   *    changed since the last check
   *    ([[SampleDeliveryRequestDataService.fetchDirtyManualAllocationContactIds]],
   *    filtered directly on that record's own modifiedon).
   *  - Eligibility: the "aa" N:N association itself has no timestamp of its
   *    own (confirmed against live UAT metadata), so which SPECIFIC contact
   *    changed can't be read directly — but associating/disassociating DOES
   *    bump the parent CONFIG's modifiedon (confirmed live), so which
   *    CONFIGS changed is a normal incremental filter
   *    ([[SampleDeliveryRequestDataService.fetchDirtyManualAllocationConfigIds]]).
   *    For that (usually empty) set of changed configs, their full current
   *    eligible-contact list is re-pulled
   *    ([[SampleDeliveryRequestDataService.fetchEligibleContactIdsForConfigs]]
   *    — still no fan-out, same "ca" branch removed as the bulk query) and
   *    diffed against manualAllocationsByContact to find who's newly
   *    eligible.
   * Both checks refresh their found contacts through
   * syncManualAllocationsForContact's own already-correct single-contact
   * fetch — genuinely incremental, not a skip, and not a daily-throttled
   * full scan.
   *
   * Both checks share the SAME delta cursor (lastDeltaCheckedAt) and run
   * inside ONE try/catch, advancing that cursor exactly once at the end —
   * NOT independently per check. Splitting them into separate try/catch
   * blocks (each reading/advancing the shared cursor on its own) would be
   * unsafe: if block A advances the cursor and block B then re-reads it
   * fresh, B would silently skip whatever window it was actually supposed
   * to check. A transient failure partway through means the WHOLE pass
   * retries both checks together next time (safe/idempotent — re-checking
   * an already-handled contact/config is wasted work, not a correctness
   * bug), rather than only the specific check that failed.
   *
   * Known accepted gap: this only detects contacts newly ADDED to
   * eligibility, not ones removed — a removed contact's cached entry isn't
   * cleared. Bounded, not a correctness bug: Dynamics still validates
   * authoritatively at actual submit time regardless of what this
   * best-effort offline cache shows.
   */
  async syncManualAllocationsForPosition(forceFullSync = false, loadFromDbOnly = false): Promise<void> {
    if (!this.authService.hasFeatureAction(FeatureActionsMap.SAMPLE_DELIVERY_REQUESTS)) {
      return;
    }
    // OMNI-61507: see syncManualAllocationsForContact — skipped wholesale while
    // validation is off. This one runs during pull-sync across every mapped
    // contact, so it's the larger of the two savings.
    if (!this.isAllocationValidationEnabled) {
      return;
    }
    if (this.device.isOffline || loadFromDbOnly) {
      return;
    }
    const skuIds = this.sdrSkus.map((s) => s.skuId).filter(Boolean);
    if (!skuIds.length) return;

    let myPositions = (this.authService.user?.positions ?? []).map((p) => p.ID);
    const childPositions = (this.authService.user?.childPositions ?? []).map((p) => p.childPositionId);
    if (childPositions.length) {
      myPositions = myPositions.concat(childPositions);
    }
    myPositions = myPositions.filter(Boolean);
    if (!myPositions.length) return;

    const runFullSync = await this.dataService.shouldRunFullPositionAllocationSync(forceFullSync);
    if (runFullSync) {
      try {
        const byContact = await this.dataService.fetchManualAllocationsForPosition(myPositions, skuIds);
        const syncedAt = Date.now();
        for (const [contactId, allocations] of byContact.entries()) {
          this.manualAllocationsByContact.set(contactId, this._toSkuMap(allocations));
          this.manualAllocationsSyncedAt.set(contactId, syncedAt);
        }
        // Batched write (one/few disk.bulkInChunks calls) instead of one serial
        // saveManualAllocationsToDb await per contact - see saveManualAllocationsToDbBulk
        // for why that loop was the dominant cost of this sync stage.
        await this.dataService.saveManualAllocationsToDbBulk(byContact);
        await this.dataService.markFullPositionAllocationSyncComplete();
      } catch (err) {
        console.error('syncManualAllocationsForPosition (full) failed; per-contact on-demand fetch remains available', err);
      }
      return;
    }

    try {
      const sinceEpochMs = await this.dataService.getPositionAllocationDeltaSinceTime();
      // sinceEpochMs is only null if no full sync has ever completed — unreachable in
      // practice here (shouldRunFullPositionAllocationSync would have routed to the full-sync
      // branch above in that case), but guarded as a conditional rather than an early `return`.
      if (sinceEpochMs != null) {
        // Consumption: contacts whose own allocation record changed since the last check.
        const dirtyContactIds = await this.dataService.fetchDirtyManualAllocationContactIds(myPositions, skuIds, sinceEpochMs);
        for (const contactId of dirtyContactIds) {
          await this.syncManualAllocationsForContact(contactId, true);
        }

        // Eligibility: configs whose OWN modifiedon changed since the last check (their "aa"
        // association gained/lost a contact — see the FetchXML's own comment for why config-level
        // modifiedon is usable here even though the association row itself has none). For each
        // changed config, re-pull its full current eligible-contact list and refresh whichever
        // contacts aren't already in the cache.
        const dirtyConfigIds = await this.dataService.fetchDirtyManualAllocationConfigIds(skuIds, sinceEpochMs);
        if (dirtyConfigIds.length) {
          const eligibleContactIds = await this.dataService.fetchEligibleContactIdsForConfigs(dirtyConfigIds, myPositions, skuIds);
          const newlyEligibleContactIds = eligibleContactIds.filter((id) => !this.manualAllocationsByContact.has(id));
          for (const contactId of newlyEligibleContactIds) {
            await this.syncManualAllocationsForContact(contactId, true);
          }
        }

        // ONE shared cursor advance for both checks above — see this method's own doc comment
        // for why splitting this into two independent try/catch-and-mark blocks would be unsafe.
        await this.dataService.markPositionAllocationDeltaChecked();
      }
    } catch (err) {
      console.error('syncManualAllocationsForPosition (delta) failed; per-contact on-demand fetch remains available', err);
    }
  }

  private async _loadManualAllocationsFromDbIntoMemory(contactId: string): Promise<void> {
    try {
      const cached = await this.dataService.loadManualAllocationsFromDb(contactId);
      this.manualAllocationsByContact.set(contactId, this._toSkuMap(cached));
    } catch (err) {
      console.error('syncManualAllocationsForContact: disk snapshot read failed', err);
    }
  }

  /**
   * Builds the per-SKU lookup map. This query is NOT scoped to a single SKU
   * (it returns every currently-valid Manual config), so a customer can have
   * more than one row for the SAME SKU across different, non-overlapping
   * config periods (e.g. an expired Jan–Jun config and a current Jul–Dec
   * one) — when that happens, keep whichever row is actually usable right
   * now over one Dynamics would reject outright, so an expired row never
   * shadows a current valid one for the same SKU.
   */
  private _toSkuMap(allocations: SdrCustomerAllocation[]): Map<string, SdrCustomerAllocation[]> {
    const bySku = new Map<string, SdrCustomerAllocation[]>();
    for (const a of allocations ?? []) {
      if (!a?.skuId) continue;
      const key = a.skuId.toLowerCase();
      const rows = bySku.get(key);
      if (rows) rows.push(a);
      else bySku.set(key, [a]);
    }
    return bySku;
  }

  /**
   * Picks the row that applies on `asOf` from the rows cached for one SKU:
   * a usable one wins over one Dynamics would reject outright, so an expired
   * row never shadows a valid one. Falls back to the first row when none is
   * usable, so callers still get something to report against (isAllocationBlocking
   * then fails closed on it) rather than a null that reads as "no config at all".
   */
  private _pickAllocationFor(rows: SdrCustomerAllocation[], asOf: Date): SdrCustomerAllocation | null {
    if (!rows?.length) return null;
    return rows.find((a) => this._isUsableAt(a, asOf)) ?? rows[0];
  }

  /** Date-valid AND (associated OR already has consumption history) — the
   *  same "will Dynamics actually accept this" test used by isAllocationBlocking,
   *  kept separate for _toSkuMap's row-preference logic above. */
  private _isUsableAt(a: SdrCustomerAllocation, asOf: Date): boolean {
    const dateValid = (!a.startDate || asOf >= a.startDate) && (!a.endDate || asOf <= a.endDate);
    return dateValid && (a.isEligible || !!a.id);
  }

  /**
   * Look up the cached Manual-Distribution allocation ceiling for a
   * contact + SKU, or null when none is cached — no config exists for this
   * SKU at all, the sync hasn't completed, failed, or the device is offline;
   * all four cases return the same null here, and isAllocationBlocking
   * treats all four the same way: fail closed, not silently pass the line
   * through unchecked.
   *
   * When allocation validation is switched off (OMNI-61507) this reports null
   * unconditionally, so no allocation-derived UI can render. The guard is here
   * as well as in isAllocationBlocking because the cache is a service-singleton
   * Map that survives a mid-session config change — a row synced while the
   * config still said Yes would otherwise resurrect the "Remaining: N" chip and
   * the qty-exceeds-remaining cap.
   *
   * `remaining` is the raw server figure. Dynamics decrements it only when an
   * SDR header reaches Submitted, so a line sitting in an open Draft — on
   * this device or any other — is deliberately NOT reflected here (OMNI-61580
   * removed the local reservation ledger this used to net out here).
   */
  getAllocationForSku(contactId: string, skuId: string, asOf: Date = new Date()): SdrCustomerAllocation | null {
    if (!contactId || !skuId) return null;
    if (!this.isAllocationValidationEnabled) return null;
    const rows = this.manualAllocationsByContact.get(contactId)?.get(skuId.toLowerCase());
    return this._pickAllocationFor(rows, asOf);
  }

  /**
   * True when this line should be blocked from submission — either GoApp has
   * positively confirmed Dynamics will reject it, or there's no way to know
   * either way, and this fails closed rather than silently letting an
   * unverified line through.
   *
   * `allocation === null` (no cached config row for this SKU): ALWAYS blocks.
   * Two situations produce this, and both must block:
   *   - Confirmed zero configs: a fetch (live or disk-fallback) genuinely
   *     completed for this contact and came back with nothing for this SKU.
   *     Confirmed live against Exeevo.AllocationSR.PreOperationSRLineItem —
   *     Dynamics throws "No Allocation Config found." for a SKU with zero
   *     matching configs, it does NOT allow it through untracked.
   *   - Never actually checked: FA disabled, the live fetch failed AND the
   *     disk-snapshot fallback found nothing (e.g. this contact has never
   *     been synced while online, so there's no last-known snapshot to fall
   *     back to — see the offline/online gap this was written to close).
   *     Rather than assume "no data" means "no problem," this is treated the
   *     same as a confirmed rejection: we don't know it's safe, so it isn't
   *     submittable until it can actually be verified.
   *
   * When an allocation row IS present, two independent rules apply,
   * confirmed against the same plugin — they are NOT the same check and
   * don't share a gate:
   *   - Date window: GetAllocationConfiguration re-checks
   *     [indskr_startdate, indskr_enddate] against today on EVERY call,
   *     unconditionally — even when a Customer Allocation record already
   *     exists from when the config used to be valid. So an expired/
   *     not-yet-started config always blocks, regardless of `id`.
   *   - Eligibility (Allocation↔Contact N:N): only checked by
   *     ProcessCustomerAllocation's no-existing-record branch — once a
   *     Customer Allocation record exists (`id` set), Dynamics never
   *     re-checks it. So this only blocks when BOTH not associated AND no
   *     existing record.
   *
   * `meetingWindow` adds a THIRD rule that Dynamics does not enforce at all —
   * confirmed with the Dynamics team: the quantity plugin has no date logic,
   * and GetAllocationConfiguration only compares the config to today. So an
   * SDR raised from a meeting must additionally have a config that spans the
   * whole meeting (not merely overlaps it); GoApp is the only thing checking
   * this, which is why it lives here rather than being left to submit.
   */
  isAllocationBlocking(
    allocation: SdrCustomerAllocation | null,
    asOf: Date = new Date(),
    meetingWindow?: SdrMeetingWindow | null,
  ): boolean {
    // OMNI-61507: with validation off, nothing here applies — and this guard
    // must precede the fail-closed `!allocation` branch below, which would
    // otherwise block every line on an org that never provisioned allocation.
    if (!this.isAllocationValidationEnabled) return false;
    if (!allocation) return true;
    const dateValid =
      (!allocation.startDate || asOf >= allocation.startDate) &&
      (!allocation.endDate || asOf <= allocation.endDate);
    if (!dateValid) return true;
    if (meetingWindow) {
      const { start, end } = meetingWindow;
      if (allocation.startDate && start && allocation.startDate > start) return true;
      if (allocation.endDate && end && allocation.endDate < end) return true;
    }
    if (!allocation.isEligible && !allocation.id) return true;
    return false;
  }

  /**
   * Drop one contact's cached allocations — called by submit() on a
   * successful create, so a just-consumed allocation isn't served stale from
   * the TTL cache the next time a create form opens for this same contact
   * (see submit()'s comment). Also safe to call whenever leaving the SDR flow
   * for that contact, so a stale cache never bleeds into a different
   * visit/contact later.
   *
   * Note: between this clear and the next sync completing, every SKU for
   * this contact reads as blocked (isAllocationBlocking fails closed on the
   * resulting empty cache) — an intentional, brief cost of failing closed
   * rather than open.
   */
  clearManualAllocationsForContact(contactId: string): void {
    this.manualAllocationsByContact.delete(contactId);
    this.manualAllocationsSyncedAt.delete(contactId);
  }

  /**
   * Fresh, force-refreshed re-check immediately before a submit/finalize
   * actually commits. With no client-side pre-submit hold, this is the ONLY
   * gate between two independently-composed requests for the same
   * contact+SKU: the force-refresh picks up whatever Dynamics decremented
   * for any request that reached Submitted first, so the later one blocks
   * here rather than being rejected by the plugin mid-batch.
   *
   * Offline this can only compare against the last disk snapshot (see
   * syncManualAllocationsForContact's own offline branch) — a clearly-
   * impossible quantity is still catchable from local data alone, and
   * uploadPendingSdrs' own cap-and-retry handles the rest on reconnect.
   */
  async revalidateAllocationsBeforeSubmit(
    contactId: string,
    products: SdrProductLine[],
    asOf: Date = new Date(),
    meetingWindow?: SdrMeetingWindow | null,
  ): Promise<{ ok: boolean; blockedProducts: SdrProductLine[] }> {
    if (!contactId || !products?.length) return { ok: true, blockedProducts: [] };
    await this.syncManualAllocationsForContact(contactId, true);
    const blocked: SdrProductLine[] = [];
    for (const p of products) {
      if (!p?.skuId) continue;
      const allocation = this.getAllocationForSku(contactId, p.skuId, asOf);
      if (this.isAllocationBlocking(allocation, asOf, meetingWindow)) {
        blocked.push(p);
        continue;
      }
      if (allocation && !allocation.isUnlimited && p.qty > allocation.remaining) {
        blocked.push(p);
      }
    }
    return { ok: blocked.length === 0, blockedProducts: blocked };
  }

  get sdrSkus(): SdrPickerSku[] {
    const products = Array.isArray(this.caseManagementService?.caseProductSKU)
      ? this.caseManagementService.caseProductSKU
      : [];
    const skus = products
      .flatMap((b) =>
        (b.skus || [])
          // Only Active SKUs are eligible — Draft/Under Revision/Retired must not be selectable (OMNI-60785)- only active skus come from updated mse
          .map((sku) => ({
            skuId: sku.ID,
            skuName: sku.name,
            brandId: b.ID,
            brandName: b.name,
          })),
      );
    return this._sortSkusByConfig(skus);
  }

  /**
   * Memoized BU_ACTIVITY_GA_PRODUCT_SORT_ORDER config map — ported from
   * InlineAllocationOrderComponent._getSkuSortOrderMap (Sample Drop) so the
   * SDR product picker gets the same sort behavior instead of the raw,
   * unsorted catalog order (OMNI-60806). Config JSON:
   * [{ "id": "<guid>", "order": 1 }, ...] — matches by skuId; unmatched
   * GUIDs fall back to the alphabetical sort in _sortSkusByConfig.
   */
  private _skuSortOrderMap: Map<string, number> | null = null;

  private _getSkuSortOrderMap(): Map<string, number> {
    if (this._skuSortOrderMap) return this._skuSortOrderMap;
    this._skuSortOrderMap = new Map<string, number>();
    try {
      const configValue = this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ACTIVITY_GA_PRODUCT_SORT_ORDER);
      if (configValue && typeof configValue === 'string') {
        const parsed = JSON.parse(configValue);
        if (Array.isArray(parsed)) {
          parsed
            .filter((entry: any) => entry?.id)
            .forEach((entry: any) => {
              this._skuSortOrderMap.set(String(entry.id).trim().toLowerCase(), entry.order ?? 0);
            });
        }
      }
    } catch (e) {
      console.error('Failed to parse BU_ACTIVITY_GA_PRODUCT_SORT_ORDER config', e);
    }
    return this._skuSortOrderMap;
  }

  /** Config-ranked SKUs first (in configured order), everything else
   *  alphabetical by skuName — mirrors InlineAllocationOrderComponent.
   *  _sortAllocationsByConfig (Sample Drop) so both features sort the same way. */
  private _sortSkusByConfig(skus: SdrPickerSku[]): SdrPickerSku[] {
    const configOrder = this._getSkuSortOrderMap();
    return skus.sort((a, b) => {
      if (configOrder.size > 0) {
        const aRanked = configOrder.has(a.skuId?.toLowerCase());
        const bRanked = configOrder.has(b.skuId?.toLowerCase());
        if (aRanked && bRanked) {
          return configOrder.get(a.skuId.toLowerCase()) - configOrder.get(b.skuId.toLowerCase());
        }
        if (aRanked) return -1;
        if (bRanked) return 1;
      }
      return (a.skuName || '').localeCompare(b.skuName || '');
    });
  }

  /**
   * sdrSkus restricted to SKUs with a non-blocking Manual-Distribution
   * allocation for this contact (OMNI-61531) — the product picker must only
   * offer products the customer is actually eligible for, not the entire
   * device-wide catalog. Relies on manualAllocationsByContact already being
   * populated for this contact via syncManualAllocationsForContact; until
   * that resolves (or when it comes back empty), isAllocationBlocking fails
   * closed and every SKU is excluded here — same fail-closed behavior the
   * per-line eligibility check already uses, just applied before add instead
   * of only after.
   */
  /**
   * The single source for a product line's allocation display fields, shared by
   * both SDR forms. Everything blanks together when isAllocationBlocking is
   * true: `_toSkuMap` can still return an outdated row's figures, and a stale
   * "Remaining: N" beside a "not available" state contradicts it.
   */
  resolveAllocationDisplay(
    contactId: string,
    skuId: string,
    asOf: Date = new Date(),
    meetingWindow?: SdrMeetingWindow | null,
  ): SdrAllocationDisplay {
    const allocation = this.getAllocationForSku(contactId, skuId, asOf);
    const notEligible = this.isAllocationBlocking(allocation, asOf, meetingWindow);
    const usable = !notEligible && allocation ? allocation : null;
    return {
      remaining: usable ? usable.remaining : null,
      unlimited: !!usable?.isUnlimited,
      notEligible,
      eligible: usable ? usable.limit : null,
      start: usable?.startDate ?? null,
      end: usable?.endDate ?? null,
    };
  }

  applyAllocationDisplay(product: SdrProductLine, display: SdrAllocationDisplay): void {
    product.allocationRemaining = display.remaining;
    product.allocationUnlimited = display.unlimited;
    product.allocationNotEligible = display.notEligible;
    product.allocationEligible = display.eligible;
    product.allocationStart = display.start;
    product.allocationEnd = display.end;
  }

  /** `limit` is `indskr_eligibilitylimit ?? 0`, never null, so it can't alone
   *  say whether there is anything to show (OMNI-62147). */
  hasAllocationDetails(product: SdrProductLine): boolean {
    if (product?.allocationEligible == null) return false;
    return !product.allocationUnlimited || !!this.formatAllocationValidity(product);
  }

  formatAllocationValidity(product: SdrProductLine): string {
    const start = product?.allocationStart;
    const end = product?.allocationEnd;
    if (!start && !end) return '';
    const fmt = (d: Date) =>
      this.dateTimeFormatsService.formatDate(d, this.dateTimeFormatsService.dateToUpper);
    if (start && end) return `${fmt(start)} - ${fmt(end)}`;
    return fmt(start ?? end);
  }

  getEligibleSkusForContact(
    contactId: string,
    asOf: Date = new Date(),
    meetingWindow?: SdrMeetingWindow | null,
  ): SdrPickerSku[] {
    if (!contactId) return [];
    return this.sdrSkus.filter(
      (sku) =>
        !this.isAllocationBlocking(
          this.getAllocationForSku(contactId, sku.skuId, asOf),
          asOf,
          meetingWindow,
        ),
    );
  }

  getProductLimitsMap(): Map<string, SdrIoConfigLimit> {
    const map = new Map<string, SdrIoConfigLimit>();
    const raw = this.authService.getIOConfigurationValue(
      IoConfigurationFeatureCodes.BU_SDR_GA_PRODUCTLIMITS,
    );
    if (!raw) return map;
    try {
      const limits: SdrIoConfigLimit[] = JSON.parse(raw);
      for (const entry of limits) {
        // Keys are stored lower-cased: getIOConfigurationValue lower-cases TEXT
        // config values, so the GUIDs arrive lower-cased. Callers must look up
        // by `skuId.toLowerCase()` so casing differences never miss a match.
        if (entry?.id) map.set(entry.id.toLowerCase(), entry);
      }
    } catch {
      // malformed iO config — silently return empty map
    }
    return map;
  }

  /**
   * Resolves the BU_ACTIVITY_GA_NOTES_ATTACHMENTS iO config into render + mandatory
   * flags. The value is a JSON array keyed by activityType:
   *   [{ "activityType": "sampleDeliveryRequest",
   *      "notes": {"mandatory": bool}, "attachments": {"mandatory": bool} }, …]
   * `notes` and `attachments` are independent — either, both, or neither may be
   * present. The matched entry's keys decide which inputs render; each key's
   * `mandatory` gates submit independently.
   *
   * Matching is case-insensitive. getIOConfigurationValue already lower-cases
   * TEXT values, so keys/values arrive lower-cased; we re-normalise entry keys
   * defensively so the resolver is correct regardless. The plural activityType
   * "sampledeliveryrequests" is tolerated alongside the singular.
   */
  getNotesConfig(): SdrNotesConfig {
    return parseNotesAttachmentsConfig(
      this.authService.getIOConfigurationValue(IoConfigurationFeatureCodes.BU_ACTIVITY_GA_NOTES_ATTACHMENTS),
      ['sampledeliveryrequest', 'sampledeliveryrequests'],
    );
  }

  /**
   * Edit-mode section title — derived from the resolved iO config. Read-mode
   * uses [[deriveNotesSectionTitleKey]] which scans the records instead.
   */
  getNotesSectionTitleKeyFromConfig(config: SdrNotesConfig): string {
    if (config?.showNotes && config?.showAttachments) return 'SDR_NOTES_AND_ATTACHMENTS';
    if (config?.showAttachments) return 'SDR_ATTACHMENTS';
    return 'NOTES';
  }

  /**
   * Read-mode section title — walks the records once. Any note with
   * non-whitespace `noteText` counts as text-present; any note with
   * `hasDocument === true` counts as attachment-present. Returns
   * `SDR_NOTES_AND_ATTACHMENTS` / `SDR_ATTACHMENTS` / `NOTES` based on which
   * buckets are non-empty. Empty / null array → `NOTES` (read-mode visibility
   * hides the whole section in that case, so the value is never rendered).
   */
  deriveNotesSectionTitleKey(existingNotes: IONote[]): string {
    let hasText = false;
    let hasAttachment = false;
    for (const n of existingNotes ?? []) {
      if (n?.noteText?.trim()) hasText = true;
      if (n?.hasDocument) hasAttachment = true;
      if (hasText && hasAttachment) break;
    }
    if (hasText && hasAttachment) return 'SDR_NOTES_AND_ATTACHMENTS';
    if (hasAttachment) return 'SDR_ATTACHMENTS';
    return 'NOTES';
  }

  initFormState(
    contactId: string,
    contactName: string,
    deliveryAddressId: string,
    deliveryAddressLabel: string,
    emailId: string,
    emailLabel: string,
  ): SdrFormState {
    return {
      contactId,
      contactName,
      deliveryAddressId,
      deliveryAddressLabel,
      emailId,
      emailLabel,
      products: [],
      useAlternateAddress: false,
      alternateDeliveryAddress: '',
      useAlternateEmail: false,
      alternateEmail: '',
    };
  }

  isFormValid(state: SdrFormState, notesGate?: SdrNotesGate): boolean {
    return this.validateForSubmit(state, notesGate) === 'ok';
  }

  /**
   * OMNI-60495: email is mandatory by default; a BU can turn this off via the
   * BU_SDR_GA_EMAILMANDATORY iO config, mirroring the existing YES_NO iO-config
   * idiom (e.g. BU_ALLOCATIONORDER_GA_ESIGNATURENECESSARY in sample.service.ts),
   * compared case-insensitively against "no" — confirmed live that this BU's
   * config record is stored as dataType TEXT, so getIOConfigurationValue
   * lower-cases it ("No" comes back as "no"); comparing lower-cased on our
   * side is correct regardless of the record's actual dataType. Anything other
   * than an explicit "no" (unconfigured, or authService unavailable in
   * pure-method tests) preserves today's behavior: mandatory.
   */
  get isEmailMandatory(): boolean {
    const raw = this.authService?.getIOConfigurationValue(IoConfigurationFeatureCodes.BU_SDR_GA_EMAILMANDATORY);
    return raw?.toLowerCase() !== 'no';
  }

  /**
   * OMNI-61507: master switch for the whole client-side allocation check.
   * Opt-IN, deliberately the opposite polarity to isEmailMandatory above:
   * unconfigured means OFF. The Dynamics allocation plugins
   * (Exeevo.AllocationSR.*) can be disabled per org, and their state isn't
   * visible to the client — so with no config record GoApp must not invent a
   * validation the backend isn't running. Safe to default off because the
   * check itself is unreleased (landed 2026-07-13 under OMNI-59706, ships in
   * 25.0), so no org depends on today's behavior.
   *
   * Compared lower-cased on our side because getIOConfigurationValue only
   * lower-cases TEXT values and hands back YES_NO records raw — so "Yes"
   * must match regardless of how the record's dataType was authored (the
   * same trap isEmailMandatory documents above).
   */
  get isAllocationValidationEnabled(): boolean {
    const raw = this.authService?.getIOConfigurationValue(
      IoConfigurationFeatureCodes.BU_SDR_GA_ALLOCATIONVALIDATION,
    );
    console.log('BU_SDR_GA_ALLOCATIONVALIDATION:', raw);
    return raw?.toLowerCase() === 'yes';
  }

  /**
   * Per-section validity flags — used by the create form to render inline
   * required-field chrome (asterisks + red separator + "This is a required
   * field" text) on the sections the user has already touched. Mirrors the
   * `ind-form-field` `isEmptyRequiredField` semantics: a section is invalid
   * when its value is empty, regardless of touched state — the create form
   * layers the touched gate on top.
   */
  getFieldValidity(state: SdrFormState): {
    customer: boolean;
    deliveryAddress: boolean;
    email: boolean;
    products: boolean;
  } {
    return {
      customer: !!state?.contactId,
      deliveryAddress:
        !!state?.deliveryAddressId ||
        (!!state?.useAlternateAddress && !!state.alternateDeliveryAddress?.trim()),
      email:
        !this.isEmailMandatory ||
        !!state?.emailId ||
        (!!state?.useAlternateEmail && !!state.alternateEmail?.trim()),
      products: !!state?.products?.length,
    };
  }

  /**
   * Per-line qty validity, shared by the create form's inline error chrome and
   * the submit gate so the two never disagree. Mirrors Sample-Drop (IAO)
   * semantics:
   *   - 'notEligible': GoApp has positively confirmed (via the Allocation↔
   *                   Contact N:N outer join) that a Manual-Distribution
   *                   config exists for this SKU but this customer isn't
   *                   associated with it, and no Customer Allocation record
   *                   exists yet either — mirrors Dynamics' "Customer is not
   *                   eligible..." rejection. Checked first and independent
   *                   of quantity, since no qty would be acceptable here.
   *   - 'zero'      : qty < 1 — never submittable (§11.6 — holds even when the
   *                   configured min is 0, matching the qty-0 blocker test).
   *   - 'range'     : a min/max iO config is present (maxQty != null) and the
   *                   qty falls outside [minQty, maxQty]. Only reachable by
   *                   typing — the stepper and dropdown are clamped to the
   *                   configured band.
   *   - 'allocation': the customer's Manual-Distribution Customer Allocation
   *                   remaining balance is known (allocationRemaining set,
   *                   not unlimited) and qty exceeds it. This is a best-effort
   *                   client-side pre-check (OMNI-59706) — Dynamics remains
   *                   authoritative at submit time regardless.
   *   - null        : qty is acceptable.
   */
  getProductQtyError(product: SdrProductLine): 'notEligible' | 'zero' | 'range' | 'allocation' | null {
    if (product.allocationNotEligible) return 'notEligible';
    if (!product?.qty || product.qty < 1) return 'zero';
    if (product.hasLimit) {
      const belowMin = product.qty < product.minQty;
      const aboveMax = product.maxQty !== null && product.qty > product.maxQty;
      if (belowMin || aboveMax) return 'range';
    }
    if (
      !product.allocationUnlimited &&
      product.allocationRemaining != null &&
      product.qty > product.allocationRemaining
    ) {
      return 'allocation';
    }
    return null;
  }

  /**
   * Returns the first blocking reason a submit attempt would fail for, or 'ok'.
   * Order matters (§11.6 / §11.10.5): required-field gaps win, then a mandatory
   * notes/attachment gap, then any per-line qty problem (qty-0 OR out-of-band
   * for configured lines).
   */
  validateForSubmit(
    state: SdrFormState,
    notesGate?: SdrNotesGate,
  ): 'requiredFields' | 'invalidEmail' | 'notes' | 'productQty' | 'ok' {
    // Address / email are satisfied by either a saved lookup id OR a non-empty
    // free-text alternate value (the two are mutually exclusive per field).
    const hasAddress =
      !!state?.deliveryAddressId ||
      (!!state?.useAlternateAddress && !!state.alternateDeliveryAddress?.trim());
    const hasEmail =
      !this.isEmailMandatory ||
      !!state?.emailId ||
      (!!state?.useAlternateEmail && !!state.alternateEmail?.trim());
    if (!state?.contactId || !hasAddress || !hasEmail || !state.products?.length) {
      return 'requiredFields';
    }
    if (state.useAlternateEmail && state.alternateEmail?.trim() && !isValidEmail(state.alternateEmail)) {
      return 'invalidEmail';
    }
    const notesUnmet = !!notesGate?.notesMandatory && !notesGate.notesSatisfied;
    const attachmentsUnmet = !!notesGate?.attachmentsMandatory && !notesGate.attachmentsSatisfied;
    if (notesUnmet || attachmentsUnmet) {
      return 'notes';
    }
    if (state.products.some((p) => this.getProductQtyError(p) !== null)) {
      return 'productQty';
    }
    return 'ok';
  }

  /**
   * Submit the create form. Staged notes (composer text + optional attachment)
   * are passed through to the data service so the SDR + its annotations land
   * in a single atomic batch — see SampleDeliveryRequestDataService.createSdr.
   * Returns both the freshly-saved SDR and the IONote[] built from the batch
   * results so the caller can hand them straight to the detail view without a
   * follow-up fetch.
   */
  async submit(state: SdrFormState, stagedNotes: StagedSdrNote[] = [], appointmentId: string | undefined, attemptId: string): Promise<SdrCreateResult> {
    // Fresh re-check right before this actually commits — closes the race
    // where this draft and a sibling draft for the same contact+SKU were
    // both composed against the same stale "remaining" snapshot. Runs
    // BEFORE any of submittingIds/pendingReconcileIds bookkeeping below, so
    // a block behaves as if submit was never attempted.
    const preflight = await this.revalidateAllocationsBeforeSubmit(state.contactId, state.products);
    if (!preflight.ok) throw new SdrAllocationExceededError(preflight.blockedProducts);
    // attemptId is the caller's own client-generated SDR id (minted once per
    // draft, before the first submit attempt) — remembered here so a submit that
    // throws AFTER the server committed can be reconciled by this id rather than
    // re-posted as a duplicate. Cleared on success below; left set on throw for
    // reconcileSubmit().
    this.submittingIds.add(attemptId);
    this.pendingReconcileIds.add(attemptId);
    try {
      const result = await this.dataService.createSdr({
        sdrId: attemptId,
        contactId: state.contactId,
        deliveryAddress: state.useAlternateAddress ? '' : state.deliveryAddressId,
        email: state.useAlternateEmail ? '' : state.emailId,
        products: state.products,
        notes: stagedNotes,
        alternateAddress: state.useAlternateAddress ? state.alternateDeliveryAddress : undefined,
        alternateEmail: state.useAlternateEmail ? state.alternateEmail : undefined,
        // OMNI-61002: links the SDR to the meeting it was raised from, when
        // supplied (the standalone create form never passes one). Optional so
        // the standalone form's existing call sites are unaffected.
        appointmentId,
        // Create Active, add lines/notes, THEN flip to Submitted — Dynamics
        // rejects every line create against an already-Submitted header (see
        // createSdr's own doc comment). This is submit()'s only call site, so
        // scoping the opt-in here (not as createSdr's new default) leaves
        // uploadPendingSdrs' fresh-create branch — shared with the inline
        // flow's fully-offline-then-sync corner case — untouched.
        createDraftFirst: true,
      });
      this.submittedSdr = result.sdr;
      // Submit confirmed — there is no orphan to reconcile.
      this.pendingReconcileIds.delete(attemptId);
      // Any accepted line just consumed part of this contact's Manual-
      // Distribution allocation server-side (Dynamics decrements
      // indskr_totalsamplesremaining synchronously in the same plugin
      // transaction). Drop the cache rather than leave its pre-submit
      // snapshot in place — otherwise a create form reopened for this same
      // contact within MANUAL_ALLOCATION_CACHE_TTL_MS would reuse stale
      // "remaining" figures and let a now-exhausted SKU through GoApp's own
      // pre-check again, only for Dynamics to reject it a second time.
      this.clearManualAllocationsForContact(state.contactId);
      // Persist the created SDR to the offline caches. Wrapped so a cache failure
      // never surfaces as a submit error — the record is already saved server-side.
      try {
        await this.cacheCreatedSdr(result.sdr);
      } catch (err) {
        console.error('SDR offline cache after create failed', err);
      }
      return result;
    } finally {
      this.submittingIds.delete(attemptId);
    }
  }

  /**
   * Drop a remembered failed-submit id for the given attempt. Called when its
   * owning form discards the draft, so its own pending reconcile can never
   * bleed into a later, unrelated submit reusing the same id (ids are GUIDs, so
   * this is purely defensive). Scoped to `id` — never touches another form's
   * in-flight or pending-reconcile attempt. (App reload clears everything for
   * free — this is in-memory only.)
   */
  clearPendingReconcile(id: string): void {
    this.pendingReconcileIds.delete(id);
  }

  /**
   * Reconcile a failed submit against the server before a blind retry. A network
   * drop on the RESPONSE leg can leave the record committed server-side even
   * though the client saw failure; re-posting would duplicate it (a retry mints a
   * new client id). The SDR id is client-generated and embedded in the create
   * batch, so a point lookup settles it:
   *   - found     → the submit actually succeeded; cache + return it so the caller
   *                 navigates to the detail instead of re-posting.
   *   - not found → the record never committed; clear the pending id so a fresh
   *                 submit is safe.
   *   - lookup throws (server still unreachable) → leave the pending id set and
   *     rethrow; the caller keeps the user on the form to try again.
   * Returns null when there is nothing to reconcile for this id.
   */
  async reconcileSubmit(id: string): Promise<SampleDeliveryRequest | null> {
    if (!this.pendingReconcileIds.has(id)) return null;
    // If this throws, the id stays in pendingReconcileIds (cleared only on a
    // resolved lookup below) and the error propagates to the caller by design.
    const sdr = await this.dataService.fetchSdrById(id);
    if (sdr) {
      this.submittedSdr = sdr;
      this.pendingReconcileIds.delete(id);
      // The submit that threw actually committed server-side (this lookup
      // just confirmed it) — same as submit()'s own success path: drop the
      // cache so the next read re-fetches the now-decremented remaining.
      this.clearManualAllocationsForContact(sdr.contactId);
      try {
        await this.cacheCreatedSdr(sdr);
      } catch (err) {
        console.error('SDR offline cache after reconcile failed', err);
      }
      return sdr;
    }
    // Confirmed absent — the earlier submit truly failed; a fresh post is safe.
    this.pendingReconcileIds.delete(id);
    return null;
  }

  /**
   * After a successful create, seed the offline cache so the new SDR behaves
   * offline immediately, before the next `syncRepSdrs`: upsert it into the
   * device-wide `allSdrs` cache (+ observable) and persist it to its own
   * sdr_record_<id> doc (one write — the record already carries full detail,
   * incl. notes, so there is no separate detail-doc write anymore) so it
   * appears in the offline timeline and is locatable by the notification path
   * right away. The created record is `notesLoaded`, so the detail view won't
   * re-fetch it.
   *
   * EXCEPTION — offline-created (`sdr.pendingSync === true`): `createSdrOffline`
   * already wrote the sdr_record_<id> doc directly, and that write carries
   * `pendingFormPayload` (the exact payload uploadPendingSdrs() replays on
   * reconnect). The `SampleDeliveryRequest` instance returned by
   * createSdrOffline does NOT carry pendingFormPayload as an own property (the
   * constructor only maps known Dynamics-shaped fields) — so calling
   * saveSdrRecordToDb(sdr) here would overwrite that doc with a version
   * missing pendingFormPayload entirely, permanently losing the offline SDR:
   * uploadPendingSdrs finds the doc (pendingSync still true) but can't
   * extract a payload from it, so it silently skips forever (OMNI-59706 —
   * offline SDRs never reaching Dynamics). Only the in-memory allSdrs cache
   * needs updating in this case; the disk doc is already correct.
   *
   * Public so callers that create/finalize an SDR via the data service
   * directly (bypassing submit()/submitOffline()) — e.g. the inline
   * create-draft-in-parent flow, see InlineSampleDeliveryRequestComponent.submit()
   * — can seed this same cache instead of leaving allSdrs unaware of the
   * record (OMNI-61497 follow-up: offline-created SDR vanished from the
   * meeting's Request tab because fetchSdrsForMeeting's offline branch reads
   * only from allSdrs).
   */
  async cacheCreatedSdr(sdr: SampleDeliveryRequest): Promise<void> {
    if (!sdr?.id) return;
    const idx = this.allSdrs.findIndex((s) => s.id === sdr.id);
    if (idx >= 0) this.allSdrs[idx] = sdr;
    else this.allSdrs = [sdr, ...this.allSdrs];
    if (!sdr.pendingSync) {
      await this.dataService.saveSdrRecordToDb(sdr);
    }
    this.allSdrs$.next(this.allSdrs);
  }

  /**
   * Offline sibling of submit(): builds the same createSdr payload, persists it
   * as a pendingSync doc via the data service, and seeds the SAME display caches
   * submit() does (cacheCreatedSdr) so the offline SDR shows in the meeting
   * Request tab + timeline immediately — before uploadPendingSdrs replays it on
   * reconnect. Returns the same SdrCreateResult shape as submit() so the caller
   * handles online and offline identically. (OMNI-61002)
   */
  async submitOffline(
    state: SdrFormState,
    stagedNotes: StagedSdrNote[] = [],
    appointmentId: string | undefined,
    attemptId: string,
  ): Promise<SdrCreateResult> {
    // Same fresh re-check as submit() — best-effort while offline (it can
    // only compare against the last disk snapshot, via
    // syncManualAllocationsForContact's own offline branch), but a clearly-
    // impossible quantity is still catchable from local data alone.
    const preflight = await this.revalidateAllocationsBeforeSubmit(state.contactId, state.products);
    if (!preflight.ok) throw new SdrAllocationExceededError(preflight.blockedProducts);
    this.submittingIds.add(attemptId);
    try {
      const sdr = this.dataService.createSdrOffline({
        sdrId: attemptId,
        contactId: state.contactId,
        deliveryAddress: state.useAlternateAddress ? '' : state.deliveryAddressId,
        email: state.useAlternateEmail ? '' : state.emailId,
        products: state.products,
        notes: stagedNotes,
        alternateAddress: state.useAlternateAddress ? state.alternateDeliveryAddress : undefined,
        alternateEmail: state.useAlternateEmail ? state.alternateEmail : undefined,
        appointmentId,
        // Offline has no server round-trip to resolve these ids into display
        // names — without passing the labels through, the read-only row shows
        // blank customer/address/email even though the ids are stored correctly.
        contactName: state.contactName,
        deliveryAddressLabel: state.useAlternateAddress ? '' : state.deliveryAddressLabel,
        emailLabel: state.useAlternateEmail ? '' : state.emailLabel,
      });
      this.submittedSdr = sdr;
      try {
        await this.cacheCreatedSdr(sdr);
      } catch (err) {
        console.error('SDR offline cache after offline create failed', err);
      }
      // Offline has no server round-trip, so there's nothing to validate against
      // yet — every line is optimistically "accepted" until uploadPendingSdrs
      // actually replays it online (where a line can still be rejected for
      // real, same as any online submit).
      return {
        sdr,
        notes: sdr.notes ?? [],
        lineResults: state.products.map((product) => ({ product, status: 'accepted' as const })),
        failedNoteCount: 0,
      };
    } finally {
      this.submittingIds.delete(attemptId);
    }
  }

  /**
   * SDRs raised from a given meeting (Request tab). Merges the local cache
   * (pending offline SDRs + already-synced ones seeded by cacheCreatedSdr) with
   * the server's per-appointment list, de-duped by id with the server winning on
   * conflict. Offline, or if the server fetch fails, returns the cache slice so
   * a just-created offline SDR still shows. Server rows depend on OMNI-61003
   * (the appointment lookup); until it lands `fetchSdrsForAppointment` returns []
   * and only the offline/cache SDRs surface. (OMNI-61002)
   *
   * IMPORTANT: a successful server fetch is merged back into `allSdrs` and
   * persisted (not just returned) — otherwise SDRs that were only ever seen via
   * this per-meeting server fetch (as opposed to being created this session via
   * cacheCreatedSdr) never enter the offline cache at all. Without this, going
   * offline after simply opening the meeting made every pre-existing SDR
   * "disappear" the moment a new one was created offline (the offline branch
   * above only had that one fresh record to fall back on).
   */
  async fetchSdrsForMeeting(appointmentId: string): Promise<SampleDeliveryRequest[]> {
    if (!appointmentId) return [];
    // Guarantee allSdrs reflects local disk before reading it, same as Sample
    // Drop's activityService.activities is always already loaded by the time
    // a meeting can open — a no-op once syncRepSdrs (or an earlier call here)
    // has already hydrated it this session.
    await this._ensureAllSdrsHydrated();
    const cached = (this.allSdrs || []).filter((s) => s.appointmentId === appointmentId);
    if (this.device.isOffline) return this._sortByCreatedOnDesc(cached);

    let server: SampleDeliveryRequest[] = [];
    try {
      server = await this.dataService.fetchSdrsForAppointment(appointmentId);
    } catch (err) {
      console.error('fetchSdrsForMeeting: server fetch failed, falling back to cache', err);
      return this._sortByCreatedOnDesc(cached);
    }

    // Merge into the FULL allSdrs cache (not just this meeting's slice) so SDRs
    // belonging to other meetings/contacts already in the cache aren't dropped.
    const byId = new Map<string, SampleDeliveryRequest>();
    for (const s of this.allSdrs || []) byId.set(s.id, s);
    for (const s of server) byId.set(s.id, s); // server wins on conflict
    this.allSdrs = Array.from(byId.values());
    this.allSdrs$.next(this.allSdrs);
    try {
      // Only persist the records that actually came back from this fetch —
      // not the whole merged cache. Every other SDR in `allSdrs` is untouched
      // by this meeting-open and already has its own up-to-date doc.
      await Promise.all(server.map((s) => this.dataService.saveSdrRecordToDb(s)));
    } catch (err) {
      console.error('fetchSdrsForMeeting: failed to persist fetched SDR records', err);
    }

    return this._sortByCreatedOnDesc(this.allSdrs.filter((s) => s.appointmentId === appointmentId));
  }

  private _sortByCreatedOnDesc(sdrs: SampleDeliveryRequest[]): SampleDeliveryRequest[] {
    return [...sdrs].sort((a, b) => {
      const at = a.createdOn ? a.createdOn.getTime() : 0;
      const bt = b.createdOn ? b.createdOn.getTime() : 0;
      return bt - at;
    });
  }
}
